import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { compressImage } from '../../../lib/imageCompression'
import { Eye, EyeOff, Music, Plus, Trash2, Camera, Upload, Check } from 'lucide-react'

const BUCKET = 'wedding-photos'

const TA_BASE = { width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', padding:'0.75rem', border:'1.5px solid #E5E0D8', borderRadius:'0.75rem', outline:'none', resize:'vertical', lineHeight:1.7, boxSizing:'border-box', background:'white', color:'#1A1A1A', transition:'border-color 0.18s' }

function WordCount({ text, limit }) {
  const words = (text||'').trim().split(/\s+/).filter(Boolean).length
  return <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color: words > limit ? '#C4826A' : '#BCBCBC' }}>{words}/{limit} words</span>
}

function TextArea({ value, onChange, placeholder, rows=4, limit }) {
  return (
    <div>
      <textarea value={value||''} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows}
        style={TA_BASE}
        onFocus={e=>e.target.style.borderColor='#C4826A'}
        onBlur={e=>e.target.style.borderColor='#E5E0D8'}/>
      {limit && <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'0.2rem' }}><WordCount text={value} limit={limit}/></div>}
    </div>
  )
}

function SectionLabel({ num, title, desc }) {
  return (
    <div style={{ marginBottom:'0.875rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.2rem' }}>
        <span style={{ width:22, height:22, background:'#C4826A', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Gibs, serif', fontSize:'0.7rem', color:'white', flexShrink:0 }}>{num}</span>
        <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:0 }}>{title}</h3>
      </div>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0 28px', lineHeight:1.55 }}>{desc}</p>
    </div>
  )
}

function SplitFields({ hisVal, herVal, onHis, onHer, hisPlaceholder, herPlaceholder, limit, p1, p2 }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(230px,1fr))', gap:'0.875rem' }}>
      <div>
        <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#C4826A', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:'0.4rem' }}>{p1} 💍</label>
        <TextArea value={hisVal} onChange={onHis} placeholder={hisPlaceholder} rows={5} limit={limit}/>
      </div>
      <div>
        <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#9BAF9B', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:'0.4rem' }}>{p2} 💐</label>
        <TextArea value={herVal} onChange={onHer} placeholder={herPlaceholder} rows={5} limit={limit}/>
      </div>
    </div>
  )
}

export default function OurStoryTab() {
  const { wedding, isPremium } = useWedding()

  const [story, setStory] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const [mode, setMode] = useState('guided')
  const [milestones, setMilestones] = useState([])
  const saveTimer = useRef(null)
  const storyRef = useRef(null)
  const milestonesRef = useRef([])

  const [studioPhotos, setStudioPhotos] = useState([])
  const [coverUrl, setCoverUrl] = useState('')
  const [coupleSurname, setCoupleSurname] = useState('')
  const [uploadingCover, setUploadingCover] = useState(false)
  const [metaSaved, setMetaSaved] = useState(false)
  const surnameTimer = useRef(null)

  useEffect(() => {
    if (!wedding?.id) return
    supabase.from('wedding_story').select('*').eq('wedding_id', wedding.id).maybeSingle()
      .then(({ data }) => {
        if (data) { setStory(data); setMode(data.write_mode||'guided'); setMilestones(data.milestones||[]); storyRef.current=data; milestonesRef.current=data.milestones||[] }
        else { const s={wedding_id:wedding.id,published:false}; setStory(s); storyRef.current=s }
        setLoading(false)
      })
    setCoverUrl(wedding.story_cover_url || '')
    setCoupleSurname(wedding.couple_surname || '')
    supabase.from('photos').select('id,public_url').eq('wedding_id', wedding.id).eq('is_studio_photo', true).order('created_at', { ascending: false })
      .then(({ data }) => setStudioPhotos(data || []))
  }, [wedding?.id])

  async function saveCoverUrl(url) {
    setCoverUrl(url)
    await supabase.from('weddings').update({ story_cover_url: url }).eq('id', wedding.id)
    setMetaSaved(true); setTimeout(() => setMetaSaved(false), 2000)
  }

  async function uploadCoverPhoto(e) {
    const raw = e.target.files?.[0]; if (!raw) return
    setUploadingCover(true)
    let file = raw
    try { file = await compressImage(raw) } catch { file = raw }   // shrink before upload
    const ext = file.name.split('.').pop()
    const path = `${wedding.id}/cover-${Date.now()}.${ext}`
    const { error } = await supabase.storage.from(BUCKET).upload(path, file)
    if (!error) {
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
      await supabase.from('photos').insert({ wedding_id: wedding.id, public_url: data.publicUrl, storage_path: path, approved: true, is_studio_photo: true, visible_on_portal: true })
      await saveCoverUrl(data.publicUrl)
      supabase.from('photos').select('id,public_url').eq('wedding_id', wedding.id).eq('is_studio_photo', true).order('created_at', { ascending: false })
        .then(({ data: d }) => setStudioPhotos(d || []))
    }
    setUploadingCover(false)
    e.target.value = ''
  }

  function handleSurnameChange(val) {
    setCoupleSurname(val)
    clearTimeout(surnameTimer.current)
    surnameTimer.current = setTimeout(async () => {
      await supabase.from('weddings').update({ couple_surname: val.trim() || null }).eq('id', wedding.id)
      setMetaSaved(true); setTimeout(() => setMetaSaved(false), 2000)
    }, 1200)
  }

  const autoSave = useCallback(async (patch, ms) => {
    if (!wedding?.id) return
    setSaving(true)
    const payload = { ...storyRef.current, ...patch, milestones: ms||milestonesRef.current, wedding_id: wedding.id, updated_at: new Date().toISOString() }
    if (storyRef.current?.id) {
      await supabase.from('wedding_story').update(payload).eq('id', storyRef.current.id)
    } else {
      const { data } = await supabase.from('wedding_story').insert(payload).select().single()
      if (data) { storyRef.current = data; setStory(data) }
    }
    setSaving(false); setLastSaved(new Date())
  }, [wedding?.id])

  if (!wedding) return (
    <div style={{ textAlign:'center', padding:'3rem' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>Loading your wedding…</p>
    </div>
  )

  function handleChange(field, value) {
    const updated = { ...storyRef.current, [field]: value }
    storyRef.current = updated
    setStory(updated)
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => autoSave(updated), 1400)
  }

  async function togglePublish() {
    const next = !storyRef.current?.published
    handleChange('published', next)
    clearTimeout(saveTimer.current)
    await autoSave({ published: next })
  }

  function addMilestone() { if (milestones.length>=4) return; const next=[...milestones,{date:'',title:'',desc:''}]; setMilestones(next); milestonesRef.current=next }
  function updateMilestone(i,field,val) {
    const next = milestones.map((m,idx)=>idx===i?{...m,[field]:val}:m)
    setMilestones(next); milestonesRef.current=next
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(()=>autoSave({},next),1400)
  }
  function removeMilestone(i) {
    const next=milestones.filter((_,idx)=>idx!==i)
    setMilestones(next); milestonesRef.current=next
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(()=>autoSave({},next),1400)
  }

  const sectionsDone = [
    !!(story?.his_how_met||story?.her_how_met),
    !!(story?.relationship_story),
    milestones.length>0,
    !!(story?.his_proposal||story?.her_proposal),
    !!(story?.his_why||story?.her_why),
    !!(story?.looking_forward),
  ].filter(Boolean).length

  // Map the groom to the man's (His 💍) column and the bride to the woman's
  // (Her 💐) column, regardless of which partner slot set up the account.
  const isP1Groom  = (wedding?.partner1_role || 'Groom') !== 'Bride'
  const p1 = (isP1Groom ? wedding?.partner1_name : wedding?.partner2_name)?.split(' ')[0] || 'Groom'  // His
  const p2 = (isP1Groom ? wedding?.partner2_name : wedding?.partner1_name)?.split(' ')[0] || 'Bride'  // Her

  if (loading) return <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC', padding:'2rem', textAlign:'center' }}>Getting your story ready…</p>

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
        <div>
          <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', margin:'0 0 0.2rem' }}>Our Story</h2>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:0 }}>
            {sectionsDone}/6 sections · {saving?'Saving…':lastSaved?`Saved ${lastSaved.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}`:'Not saved yet'}
          </p>
        </div>
        <button onClick={togglePublish} style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:story?.published?'rgba(155,175,155,0.1)':'#C4826A', color:story?.published?'#7A917A':'white', border:`1.5px solid ${story?.published?'#9BAF9B':'#C4826A'}`, borderRadius:100, padding:'0.55rem 1.125rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', flexShrink:0 }}>
          {story?.published ? <><Eye size={13}/> Published</> : <><EyeOff size={13}/> Publish Story</>}
        </button>
      </div>

      {/* Progress */}
      <div style={{ height:4, background:'#E5E0D8', borderRadius:100 }}>
        <div style={{ height:'100%', width:`${(sectionsDone/6)*100}%`, background:'#9BAF9B', borderRadius:100, transition:'width 0.4s' }}/>
      </div>

      {/* Where your story lives */}
      <div style={{ background:'#F8F5F2', border:'1px solid #E5E0D8', borderRadius:'0.875rem', padding:'1rem 1.125rem', display:'flex', flexDirection:'column', gap:'0.5rem' }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#8C7B70', textTransform:'uppercase', letterSpacing:'0.1em', margin:0 }}>Where your story appears</p>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#1A1A1A', margin:0, lineHeight:1.6 }}>
            <strong>Guest portal</strong> — clicking <em>Publish Story</em> above makes it visible to guests on your wedding page.
          </p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#1A1A1A', margin:0, lineHeight:1.6 }}>
              <strong>Okoiya website</strong> — your story can also be featured on{' '}
              <a href="/stories" target="_blank" rel="noreferrer" style={{ color:'#C4826A', textDecoration:'none', fontWeight:600 }}>okoiya.com/stories</a>
              {' '}for other couples to be inspired by, with your photos. This requires your approval.{' '}
              <Link to="/dashboard/settings" style={{ color:'#C4826A', fontWeight:700, textDecoration:'none' }}>
                Enable in Settings →
              </Link>
            </p>
        </div>
      </div>

      {/* Cover photo + surname for okoiya.com/stories */}
      <div className="card" style={{ padding:'1.25rem' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.625rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
            <Camera size={15} color="#C4826A"/>
            <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'0.95rem', margin:0 }}>Story cover photo</h3>
          </div>
          {metaSaved && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#9BAF9B' }}>Saved ✓</span>}
        </div>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:'0 0 0.875rem', lineHeight:1.6 }}>
          Pick a photo from your studio gallery or upload a new one. This is what guests see when your story appears on okoiya.com/stories.
        </p>

        {/* Photo grid */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(72px,1fr))', gap:'0.5rem', marginBottom:'1.125rem' }}>
          {studioPhotos.map(p => (
            <div key={p.id} onClick={() => saveCoverUrl(p.public_url)}
              style={{ position:'relative', borderRadius:'0.5rem', overflow:'hidden', cursor:'pointer', aspectRatio:'1', background:'#F3F0EA',
                outline: coverUrl === p.public_url ? '2.5px solid #C4826A' : '2px solid transparent', outlineOffset: -1 }}>
              <img src={p.public_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
              {coverUrl === p.public_url && (
                <div style={{ position:'absolute', top:4, right:4, width:18, height:18, background:'#C4826A', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Check size={10} color="white" strokeWidth={3}/>
                </div>
              )}
            </div>
          ))}
          <label style={{ borderRadius:'0.5rem', border:'1.5px dashed #E5E0D8', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', cursor: uploadingCover ? 'wait' : 'pointer', gap:'0.3rem', aspectRatio:'1', background:'#FAFAF8' }}>
            {uploadingCover
              ? <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:'#BCBCBC' }}>…</span>
              : <><Upload size={13} color="#BCBCBC"/><span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:'#BCBCBC' }}>Upload</span></>
            }
            <input type="file" accept="image/*" style={{ display:'none' }} onChange={uploadCoverPhoto} disabled={uploadingCover}/>
          </label>
        </div>

        {studioPhotos.length === 0 && !uploadingCover && (
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#BCBCBC', margin:'-0.5rem 0 0.875rem' }}>
            No studio photos yet — upload one above or add photos in the Studio tab.
          </p>
        )}

        {/* Shared surname */}
        <div>
          <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#8C7B70', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:'0.4rem' }}>
            Shared surname <span style={{ fontWeight:400, color:'#BCBCBC', textTransform:'none', letterSpacing:0 }}>(optional)</span>
          </label>
          <input className="input-field" placeholder="e.g. Okafor"
            value={coupleSurname} onChange={e => handleSurnameChange(e.target.value)}/>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.3rem 0 0' }}>
            Shown as "{p1} & {p2}{coupleSurname ? ' ' + coupleSurname : ''}" on okoiya.com/stories
          </p>
        </div>
      </div>

      {/* Mode toggle */}
      <div style={{ display:'flex', background:'#F3F0EA', borderRadius:100, padding:3, alignSelf:'flex-start', gap:2, flexShrink:0 }}>
        {[{v:'guided',l:'Guided Mode'},{v:'free',l:'Free Write'}].map(t=>(
          <button key={t.v} onClick={()=>{setMode(t.v);handleChange('write_mode',t.v)}}
            style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:mode===t.v?700:500, padding:'0.35rem 1rem', borderRadius:100, border:'none', cursor:'pointer', background:mode===t.v?'white':'transparent', color:mode===t.v?'#1A1A1A':'#6B6B6B', transition:'all 0.15s' }}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Free Write */}
      {mode==='free' && (
        <div className="card" style={{ padding:'1.5rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.875rem' }}>Your canvas. Write your story exactly how you want.</p>
          <TextArea value={story?.free_write_content} onChange={v=>handleChange('free_write_content',v)} placeholder="Start writing your love story here…" rows={18}/>
        </div>
      )}

      {/* Guided */}
      {mode==='guided' && (<>
        <div className="card" style={{ padding:'1.5rem' }}>
          <SectionLabel num="1" title="How It Started" desc="Tell guests where and how your paths first crossed."/>
          <SplitFields hisVal={story?.his_how_met} herVal={story?.her_how_met}
            onHis={v=>handleChange('his_how_met',v)} onHer={v=>handleChange('her_how_met',v)}
            hisPlaceholder="Where did you first see her? What was your first thought? Did you know immediately?" herPlaceholder="Where did you first see him? What do you remember most about that first encounter?" limit={150} p1={p1} p2={p2}/>
        </div>

        <div className="card" style={{ padding:'1.5rem' }}>
          <SectionLabel num="2" title="Getting to Know Each Other" desc="The journey from strangers to something more. One combined voice."/>
          <TextArea value={story?.relationship_story} onChange={v=>handleChange('relationship_story',v)}
            placeholder="What were the early days like? Was there a moment you both knew this was serious? What memory from this season do you treasure most?" rows={5} limit={200}/>
        </div>

        <div className="card" style={{ padding:'1.5rem' }}>
          <SectionLabel num="3" title="Moments That Shaped Us" desc="Up to 4 meaningful milestones — displayed as a timeline for guests."/>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
            {milestones.map((m,i)=>(
              <div key={i} style={{ background:'#FAFAF8', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.875rem', display:'flex', gap:'0.625rem', alignItems:'flex-start', flexWrap:'wrap' }}>
                <input type="date" value={m.date} onChange={e=>updateMilestone(i,'date',e.target.value)} className="input-field" style={{ width:130, flexShrink:0, fontSize:'0.78rem' }}/>
                <div style={{ flex:'1 1 140px', minWidth:120, display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                  <input placeholder="Short title (e.g. Our first trip together)" value={m.title} onChange={e=>updateMilestone(i,'title',e.target.value)} className="input-field" style={{ fontSize:'0.82rem' }}/>
                  <textarea placeholder="Brief description (max 80 words)" value={m.desc} onChange={e=>updateMilestone(i,'desc',e.target.value)} rows={2} style={{ ...TA_BASE, fontSize:'0.78rem', padding:'0.5rem 0.75rem' }}/>
                </div>
                <button onClick={()=>removeMilestone(i)} style={{ background:'none', border:'none', cursor:'pointer', color:'#BCBCBC', paddingTop:6, flexShrink:0 }}><Trash2 size={14}/></button>
              </div>
            ))}
            {milestones.length<4 && (
              <button onClick={addMilestone} className="btn-secondary" style={{ alignSelf:'flex-start', fontSize:'0.78rem', padding:'0.4rem 0.875rem', display:'flex', alignItems:'center', gap:'0.3rem' }}>
                <Plus size={12}/> Add Milestone
              </button>
            )}
          </div>
        </div>

        <div className="card" style={{ padding:'1.5rem' }}>
          <SectionLabel num="4" title="The Question" desc="The proposal. The most-read section — guests always love this one."/>
          <SplitFields hisVal={story?.his_proposal} herVal={story?.her_proposal}
            onHis={v=>handleChange('his_proposal',v)} onHer={v=>handleChange('her_proposal',v)}
            hisPlaceholder="How long did you plan it? Were you nervous? Walk us through exactly what happened." herPlaceholder="Were you surprised? What went through your mind the moment it happened? What did you say first?" limit={200} p1={p1} p2={p2}/>
        </div>

        <div className="card" style={{ padding:'1.5rem' }}>
          <SectionLabel num="5" title="Why I Chose You" desc="Not why you love each other generally — specifically. This section will make guests emotional."/>
          <SplitFields hisVal={story?.his_why} herVal={story?.her_why}
            onHis={v=>handleChange('his_why',v)} onHer={v=>handleChange('her_why',v)}
            hisPlaceholder="In your own words — not why you love her generally, but why specifically her. What is it about her that no one else has?" herPlaceholder="Same question. Why specifically him. The thing that only you know." limit={150} p1={p1} p2={p2}/>
        </div>

        <div className="card" style={{ padding:'1.5rem' }}>
          <SectionLabel num="6" title="And Now…" desc="A short closing thought as you walk into forever."/>
          <TextArea value={story?.looking_forward} onChange={v=>handleChange('looking_forward',v)}
            placeholder="One sentence, or one hundred — what does forever look like to both of you? What are you most excited to build together?" rows={4} limit={150}/>
        </div>
      </>)}

      {/* Our Song */}
      <div className="card" style={{ padding:'1.25rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.5rem' }}>
          <Music size={15} color="#C4826A"/>
          <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'0.95rem', margin:0 }}>Our Song</h3>
        </div>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:'0 0 0.625rem' }}>Paste a Spotify or YouTube link. Guests can play it while reading your story.</p>
        {isPremium ? (
          <input className="input-field" placeholder="https://open.spotify.com/track/... or https://youtu.be/..."
            value={story?.song_url||''} onChange={e=>handleChange('song_url',e.target.value)}/>
        ) : (
          <div style={{ background:'rgba(196,130,106,0.06)', border:'1px solid rgba(196,130,106,0.15)', borderRadius:'0.75rem', padding:'0.75rem 1rem' }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#C4826A', margin:0 }}>💎 Our Song is a Premium feature — upgrade to embed your wedding song for guests.</p>
          </div>
        )}
        {/* Spotify embed preview */}
        {story?.song_url?.includes('spotify.com') && (()=>{
          const match = story.song_url.match(/\/(track|album|playlist)\/([a-zA-Z0-9]+)/)
          if (!match) return null
          const [, type, id] = match
          return (
            <iframe
              style={{ borderRadius:'0.75rem', marginTop:'0.75rem', width:'100%', border:'none' }}
              src={`https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`}
              height={type==='track'?80:152}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"/>
          )
        })()}
        {story?.song_url && !story.song_url.includes('spotify.com') && story.song_url.includes('youtu') && (()=>{
          const match = story.song_url.match(/(?:youtu\.be\/|v=)([a-zA-Z0-9_-]+)/)
          if (!match) return null
          return (
            <iframe
              style={{ borderRadius:'0.75rem', marginTop:'0.75rem', width:'100%', border:'none' }}
              src={`https://www.youtube.com/embed/${match[1]}`}
              height={200}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              loading="lazy"/>
          )
        })()}
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.4rem 0 0' }}>Paste a Spotify or YouTube link — it will embed automatically for your guests.</p>
      </div>

      {!story?.published && (
        <div style={{ background:'rgba(196,130,106,0.06)', border:'1px solid rgba(196,130,106,0.2)', borderRadius:'0.75rem', padding:'0.75rem 1rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:0 }}>📌 Hidden from guests until you tap <strong>Publish Story</strong>.</p>
        </div>
      )}
    </div>
  )
}
